import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { WizardStepProps } from '@components/Wizard/Wizard';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import CircleBackground from '@assets/images/CircleBackground.png';
import { GIVONIC, INTER } from '@styles/fonts';
import { BLACK, PALE_BLUE } from '@styles/colors';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import TextInput from '@components/TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import useReferral from '@hooks/useReferral';
import { safeAreaInset, getReferralCodeFromUrl } from '@lib/telegram';

// Styled components
const ReferralContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 4vw 24px 4vw;
  background-color: ${PALE_BLUE};
  background-image: url(${CircleBackground});
  background-size: contain;
  background-position: right;
  background-repeat: no-repeat;
`

const ReferralContent = styled.div`
  padding: 2vh 4vw 3vh 4vw;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  margin-top: 4vh;
  background-color: white;
`

const ReferralTitle = styled.p`
    font-family: ${GIVONIC.bold};
    font-weight: 600;
    font-size: 16px;
    line-height: 13px;
    color: ${BLACK};
    margin: 4vh 0 2vh 0;
    text-transform: capitalize;
`

const ReferralSubTitle = styled.p`
    font-family: ${INTER.extraLight};
    font-weight: 100;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0.0012px;
    margin: 0 0 0 0;
    color: ${BLACK};
`

const FixedBottomContainer = styled.div`
  margin-top: auto; /* Push to bottom */
  padding-bottom: ${safeAreaInset().bottom === 0 ? 0 : safeAreaInset().bottom - 24}px;
`

const FixedTopContainer = styled.div`
  margin-bottom: 0; /* Ensure it stays at the top */
`

const ReferralCodeStep: React.FC<WizardStepProps> = ({ onNext }) => {
  const { postReferral, isLoading, error } = useReferral()
  const referralInputRef = useRef<HTMLInputElement>(null);
  const [referralCode, setReferralCode] = useState<string>('');
  const [isReferralCodeInvalid, setIsReferralCodeInvalid] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const code = getReferralCodeFromUrl();
    if (code) {
      setReferralCode(code);
    }
  }, []);

  const handleReferralCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReferralCode(e.target.value);
  };

  const handleContinue = async () => {
    setIsReferralCodeInvalid(false);
    // Save referral code if entered
    if (referralCode.trim()) {
      // Here you would typically save the referral code to state or context
      if (!(await postReferral({ referral_code: referralCode }))) {
        setIsReferralCodeInvalid(true);
      } else {
        onNext?.()
      }
    } else {
      onNext?.()
    }
  };

  return (
    <ReferralContainer>
      <FixedTopContainer>
        <ReferralTitle>{t('page.createWallet.steps.referralCode.referralTitle')}</ReferralTitle>
        <ReferralSubTitle>
          {t('page.createWallet.steps.referralCode.referralSubtitle')}
        </ReferralSubTitle>
      </FixedTopContainer>

      <ReferralContent>
        <TextInput
          value={referralCode}
          onChange={handleReferralCodeChange}
          placeholder={t('page.createWallet.steps.referralCode.inputPlaceholder')}
          title={t('page.createWallet.steps.referralCode.inputTitle')}
          isOptional
          isError={isReferralCodeInvalid}
          errorText={error}
          ref={referralInputRef}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              referralInputRef.current?.blur();
            }
          }}
        />
      </ReferralContent>

      <FixedBottomContainer>
        <PrimaryButton onClick={handleContinue}>{t('page.createWallet.steps.referralCode.createWallet')}</PrimaryButton>
      </FixedBottomContainer>
      <LoadingModal visible={isLoading} message={t('page.createWallet.steps.referralCode.loading')} />
    </ReferralContainer>
  );
};

export default ReferralCodeStep;
